// src/pages/CommissionSettings.js
import React, { useEffect, useState } from "react";
import api from "../services/api";
import { Form, Button, Table, Alert, Card, Badge, Spinner } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const CommissionSettings = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const isManager = user?.role === "Manager";

  const [employees, setEmployees] = useState([]);
  const [settingsMap, setSettingsMap] = useState({});
  const [mySummary, setMySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [empRes, setRes] = await Promise.all([api.get("/employees"), api.get("/commissions/settings")]);
        setEmployees(empRes.data || []);
        const map = {};
        (setRes.data || []).forEach((s) => { const email = s.employee?.email; if (email) map[email] = { perCustomerCommission: s.perCustomerCommission ?? 0, fixedSalary: s.fixedSalary ?? 0 }; });
        setSettingsMap(map);
      } catch { toast.error("Failed to load commission settings."); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  useEffect(() => {
    if (!user?.name) return;
    setSummaryLoading(true);
    api.get(`/commissions/summary/${encodeURIComponent(user.name)}`)
      .then((res) => setMySummary(res.data))
      .catch(() => {})
      .finally(() => setSummaryLoading(false));
  }, [user]);

  const fmt = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : "Not set");

  const visible = employees.filter((e) => {
    if (isAdmin) return true;
    if (isManager) return e.email === user.email || e.manager === user.name;
    return e.email === user.email;
  });

  const mySettings = user?.email ? settingsMap[user.email] || null : null;

  const onChange = (email, field, value) => {
    setSettingsMap((prev) => ({ ...prev, [email]: { perCustomerCommission: field === "perCustomerCommission" ? value : prev[email]?.perCustomerCommission ?? "", fixedSalary: field === "fixedSalary" ? value : prev[email]?.fixedSalary ?? "" } }));
  };

  const save = async (emp) => {
    try {
      const c = settingsMap[emp.email] || { perCustomerCommission: 0, fixedSalary: 0 };
      await api.put(`/commissions/settings/${encodeURIComponent(emp.email)}`, { perCustomerCommission: Number(c.perCustomerCommission || 0), fixedSalary: Number(c.fixedSalary || 0) });
      toast.success(`Updated for ${emp.name}`);
    } catch { toast.error(`Failed for ${emp.name}`); }
  };

  if (!user) return <div className="container mt-4"><Alert variant="warning">Please log in.</Alert></div>;

  return (
    <div className="container-fluid px-2 py-1">
      <div className="text-muted small mb-3">{isAdmin ? "Set compensation for each employee." : isManager ? "View your team's compensation." : "View your compensation."}</div>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-1">Your Compensation</h5>
          <div className="text-muted small">{user.name} <Badge bg="secondary" className="ms-1">{user.role}</Badge></div>
          <div className="row mt-3">
            <div className="col-md-4"><div className="small text-muted mb-1">Per Customer Commission</div><div className="fs-5 fw-semibold">{mySettings ? fmt(mySettings.perCustomerCommission) : "Not set"}</div></div>
            <div className="col-md-4"><div className="small text-muted mb-1">Fixed Monthly Salary</div><div className="fs-5 fw-semibold">{mySettings ? fmt(mySettings.fixedSalary) : "Not set"}</div></div>
            <div className="col-md-4"><div className="small text-muted mb-1">Total Payout</div>
              {summaryLoading ? <Spinner animation="border" size="sm" /> : mySummary ? (<><div className="fs-5 fw-semibold">{fmt(mySummary.totalPayout)}</div><div className="small text-muted">{mySummary.totalSales} customers × {fmt(mySummary.perCustomerCommission)} + {fmt(mySummary.fixedSalary)}</div></>) : <div className="small text-muted">No data yet.</div>}
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="d-flex justify-content-between align-items-center px-3 pt-3"><h5 className="mb-0">Employee Compensation</h5>{loading && <Spinner animation="border" size="sm" />}</div>
          <Table hover responsive className="mb-0 mt-2 align-middle">
            <thead className="table-light"><tr><th>Name</th><th>Email</th><th>Role</th><th>Per Customer</th><th>Fixed Salary</th>{isAdmin && <th style={{ width: 120 }}>Actions</th>}</tr></thead>
            <tbody>
              {visible.map((emp) => {
                const c = settingsMap[emp.email] || { perCustomerCommission: "", fixedSalary: "" };
                return (
                  <tr key={emp._id || emp.email}>
                    <td>{emp.name}</td><td>{emp.email}</td><td><Badge bg="secondary">{emp.role}</Badge></td>
                    <td style={{ maxWidth: 180 }}>{isAdmin ? <Form.Control size="sm" type="number" value={c.perCustomerCommission} placeholder="0" onChange={(e) => onChange(emp.email, "perCustomerCommission", e.target.value)} /> : fmt(c.perCustomerCommission || 0)}</td>
                    <td style={{ maxWidth: 180 }}>{isAdmin ? <Form.Control size="sm" type="number" value={c.fixedSalary} placeholder="0" onChange={(e) => onChange(emp.email, "fixedSalary", e.target.value)} /> : fmt(c.fixedSalary || 0)}</td>
                    {isAdmin && <td><Button variant="primary" size="sm" onClick={() => save(emp)}>Save</Button></td>}
                  </tr>
                );
              })}
              {!loading && visible.length === 0 && <tr><td colSpan={isAdmin ? 6 : 5} className="text-center text-muted small py-3">No employees.</td></tr>}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CommissionSettings;